import { useState } from 'react';

import { Text } from '@/design-system';
import { FeedbackFormulaAggregationType } from '@/types/schema';
import { getColorByString } from '@/utils/get-color-by-string';
import { cn } from '@/utils/tailwind';

import { FeedbackChip } from '../FeedbackChips';

export interface CompositeScorePreviewBarRow {
  id: string;
  key: string;
  weight: number;
}

export interface CompositeScorePreviewBarProps {
  rows: CompositeScorePreviewBarRow[];
  totalWeight: number;
  variant: 'score-pane' | 'score-table';
  method: FeedbackFormulaAggregationType;
}

export function CompositeScorePreviewBar({
  rows,
  totalWeight,
  variant = 'score-pane',
  method,
}: CompositeScorePreviewBarProps) {
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);

  if (rows.length === 0) return null;
  const denom = totalWeight || 1;

  return (
    <>
      <div className="flex h-3 w-full items-center rounded-sm bg-secondary">
        {rows.map((row, idx) => {
          const color = getColorByString(row.key);
          const widthBasis = Math.max(0, row.weight || 0);
          const widthPct = (widthBasis / denom) * 100;
          const isHovered = hoveredIndex === idx;
          return (
            <div
              key={row.id}
              className={cn(
                idx === 0 ? 'rounded-l-sm' : '',
                idx === rows.length - 1 ? 'rounded-r-sm' : '',
                idx < rows.length - 1 ? 'border-r-2 border-background' : '',
                'h-2 transition-all duration-200 ease-in-out',
                isHovered ? 'h-3 shadow-sm' : '',
                variant === 'score-pane' ? 'cursor-pointer' : ''
              )}
              style={{
                width: `${widthPct}%`,
                backgroundColor: color,
              }}
              onMouseEnter={() =>
                variant === 'score-pane' && setHoveredIndex(idx)
              }
              onMouseLeave={() =>
                variant === 'score-pane' && setHoveredIndex(null)
              }
            />
          );
        })}
      </div>

      <div className="mt-1 flex w-full gap-1">
        {rows.map((row, idx) => {
          const widthBasis = Math.max(0, row.weight || 0);
          const widthPct = (widthBasis / denom) * 100;
          const isHovered = hoveredIndex === idx;

          return (
            <div
              key={row.id}
              className="flex min-w-fit items-center"
              style={{
                width: `${widthPct}%`,
              }}
            >
              <div
                className={cn(
                  'flex items-center gap-1 transition-transform duration-200 ease-in-out',
                  variant === 'score-pane' ? 'cursor-pointer' : '',
                  isHovered ? 'scale-110' : ''
                )}
                onMouseEnter={() =>
                  variant === 'score-pane' && setHoveredIndex(idx)
                }
                onMouseLeave={() =>
                  variant === 'score-pane' && setHoveredIndex(null)
                }
              >
                <FeedbackChip feedback_key={row.key} />

                <Text
                  variant="xs"
                  className="rounded-sm bg-secondary p-1 font-mono text-tertiary"
                >
                  {method === 'avg'
                    ? `${Number(widthPct.toFixed(2))}%`
                    : `× ${row.weight}`}
                </Text>
              </div>
            </div>
          );
        })}
      </div>
    </>
  );
}
